import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { BaseProperties, ColorVariables } from '@group-link-one/gl-ui/sonarprint';
import { motion } from 'motion/react';
import type { IChildContTop, IStyling } from './types/Main.template.types';

// interface WrapperContentProps {}

export default {
  MainContainer: styled.div`
    /* min-width: 100vw; */
    min-height: 100vh;
    background-color: ${ColorVariables.BASE.BASE_COLOR_SENARY_BACKGROUND};
    padding: 20px;
    display: flex;
    gap: 20px;
    overflow-x: clip;

    // TODO
    > div:nth-child(2) {
      // TopBar
      > :first-child {
        z-index: 2;
      }
    }
  `,

  SidebarSpacer: styled.div`
    flex-shrink: 0;
    width: var(--sidebar-width, 80px);
    transition: width 0.3s ease-in-out;

    @media (max-width: 768px) {
      display: none;
    }
  `,

  RightContainer: styled.div`
    flex: 1;
    min-width: 0;
    background-color: ${ColorVariables.BASE.BASE_COLOR_PRIMARY_BACKGROUND};
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 20px;
    transition: gap 0.3s ease-in-out;

    /* Usa o estado IMEDIATO (is-details-active = isDetailsOpenned), não o estado
       que sobrevive à saída (is-details-opened). Assim, ao voltar, o gap cresce
       junto com a animação — em vez de saltar 1s depois (quando o onExitComplete
       limpa is-details-opened), o que causava o flick. */
    &:has(.is-details-active) {
      gap: 10px;
    }

    @media (max-width: 768px) {
      gap: 15px;
    }
  `,

  Content: styled.div`
    height: 100%;

    /* Some o conteúdo principal animando a opacidade só do wrapper (1 nó,
       composto na GPU) em vez de cada descendente — o detalhe cobre tudo com
       fundo opaco, então o resultado visual é o mesmo, com muito menos custo.
       Como é transição (e não keyframe forwards), reverte sozinha no fechamento:
       o principal reaparece enquanto o detalhe desce. */
    .gl-wrapper-content {
      transition: opacity 0.3s ease-in-out;
    }

    &.is-details-active .gl-wrapper-content {
      opacity: 0;
      pointer-events: none;
    }

    /* Estado de layout: fica ativo enquanto o detalhe está montado, INCLUSIVE
       durante a animação de saída (via onExitComplete), para o painel absoluto
       continuar posicionado e recortado (overflow) enquanto desce. */
    &.is-details-opened {
      position: relative;

      &.has-overflow-when-detailed-children-open {
        overflow: hidden;
      }

      &:not(.just-child-content) .gl-wrapper-content-detailed {
        height: fit-content;

        /* Safari-only */
        @supports (-webkit-hyphens: none) {
          height: auto;
        }
      }

      &.just-child-content .gl-wrapper-content-detailed {
        height: 100%;
      }
    }
  `,

  WrapperContent: styled.div<{ styling?: IStyling }>`
    background-color: ${ColorVariables.COLOR.COLOR_QUINARY_BACKGROUND};
    height: 100%;
    border: 1px solid ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};
    border-radius: 16px;
    padding: 20px;
    padding-bottom: 40px;

    display: flex;
    flex-direction: column;
    gap: 20px;

    transition:
      background-color 0.3s ease-in-out,
      border-color 0.3s ease-in-out;

    &.just-child-content {
      padding: 0px;
      border: none;
      gap: 0px;
    }

    ${({ styling }) => css`
      ${styling?.noBackgroundColor &&
      css`
        background-color: transparent;
      `}

      ${styling?.noBorder &&
      css`
        border: none;
      `}

      ${styling?.noPadding &&
      css`
        padding: 0px;
        padding-bottom: 40px;
      `}
    `}
  `,

  /**
   * Versão `motion` do wrapper de detalhe — mesmos estilos do `WrapperContent`,
   * mas como `motion.div` para receber `initial`/`animate`/`exit` do
   * AnimatePresence (abre subindo e fecha descendo, igual ao bottom-sheet).
   */
  WrapperContentDetailed: styled(motion.div)`
    background-color: ${ColorVariables.COLOR.COLOR_QUINARY_BACKGROUND};
    border: 1px solid ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};
    border-radius: 16px;
    padding: 20px;
    padding-bottom: 40px;

    display: flex;
    flex-direction: column;
    gap: 20px;

    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    will-change: transform;

    &.just-child-content {
      padding: 0px;
      border: none;
      gap: 0px;
    }
  `,

  ChildCont: styled.div`
    width: 100%;
    background: transparent;
    flex: 1;

    display: flex;
    flex-direction: column;
  `,

  ChildContTop: styled(motion.div)<IChildContTop>`
    display: flex;
    gap: 20px;
    flex-direction: ${({ flexDirection }) => flexDirection || 'row'};
    align-items: ${({ childContTopAlign }) => childContTopAlign || 'flex-start'};
    justify-content: space-between;

    margin-bottom: 20px;

    &.has-sticked {
      position: sticky;
      top: 5px;
      transition:
        background-color 0.3s ease-in-out,
        border 0.3s ease-in-out,
        box-shadow 0.3s ease-in-out,
        padding 0.3s ease-in-out,
        border-radius 0.3s ease-in-out;
    }

    @media (max-width: 768px) {
      gap: 10px;
    }

    @media (max-width: 600px) {
      flex-direction: column;
    }

    &.is-stuck {
      z-index: 9999;
      padding: 10px 15px;

      background-color: ${ColorVariables.BASE.BASE_COLOR_PRIMARY_BACKGROUND};
      border: 1px solid ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};
      border-radius: ${BaseProperties.RADIUS.SM};
      box-shadow: 0 3px 6px ${ColorVariables.BASE.BASE_COLOR_QUINARY_STROKE};

      &.has-sticked {
        .gl-page-title {
          height: 0px;
          opacity: 0;
        }
      }
    }
  `,
};
